<template>
  <v-container fluid>
    <v-card class="elevation-8 rounded-lg">
      <!-- Header -->
      <v-card-title class="primary white--text">
        <v-icon left color="white" size="28">mdi-chart-bar</v-icon>
        <span class="text-h5"
          >ข้อมูลสถิตินักเรียนนักศึกษา แยกตามปีการศึกษา</span
        >
        <v-spacer></v-spacer>

        <!-- View Toggle Buttons -->
        <v-btn-toggle v-model="viewMode" mandatory class="mr-2">
          <v-btn small value="table" color="white" text>
            <v-icon>mdi-table</v-icon>
          </v-btn>
          <v-btn small value="chart" color="white" text>
            <v-icon>mdi-chart-bar</v-icon>
          </v-btn>
        </v-btn-toggle>

        <!-- Refresh Button -->
        <v-btn icon color="white" @click="refreshData" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Summary Cards -->
      <v-card-text class="pb-0">
        <v-row>
          <v-col
            cols="12"
            md="3"
            v-for="(stat, index) in summaryStats"
            :key="index"
          >
            <v-card
              :color="stat.color"
              class="white--text text-center stat-card"
              elevation="4"
            >
              <v-card-text>
                <v-icon size="32" class="mb-2">{{ stat.icon }}</v-icon>
                <div class="text-h4 font-weight-bold">{{ stat.value }}</div>
                <div class="text-subtitle-1">{{ stat.title }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Chart View -->
      <v-card-text v-if="viewMode === 'chart'">
        <v-row>
          <!-- Chart Type Selector -->
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedChartType"
              :items="chartTypes"
              label="ประเภทกราฟ"
              outlined
              dense
              prepend-icon="mdi-chart-line"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedYear"
              :items="availableYears"
              label="ปีการศึกษา"
              outlined
              dense
              prepend-icon="mdi-calendar"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedLevel"
              :items="levels"
              label="ระดับชั้น"
              outlined
              dense
              prepend-icon="mdi-school"
            />
          </v-col>
        </v-row>

        <!-- Chart Container -->
        <v-card outlined class="chart-container">
          <v-card-title class="text-center">
            <span class="text-h6">{{ chartTitle }}</span>
          </v-card-title>
          <v-card-text>
            <div id="chart" style="height: 400px;">
              <apexchart
                :type="selectedChartType"
                height="400"
                :options="chartOptions"
                :series="chartSeries"
              />
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>

      <!-- Table View -->
      <v-card-text v-if="viewMode === 'table'" class="pa-0">
        <!-- Search and Filter -->
        <v-card-subtitle class="pb-0">
          <v-row align="center">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="ค้นหาวิทยาลัย หรือ รหัส"
                single-line
                hide-details
                dense
                outlined
                clearable
                class="search-field"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="filterYear"
                :items="yearFilterOptions"
                label="กรองตามปี"
                outlined
                dense
                clearable
                hide-details
              />
            </v-col>
            <v-col cols="12" md="3" class="text-right">
              <!-- Export Buttons -->
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="success" v-bind="attrs" v-on="on" small>
                    <v-icon left>mdi-download</v-icon>
                    ส่งออก
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="exportToExcel">
                    <v-list-item-icon>
                      <v-icon>mdi-file-excel</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Excel</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportToPDF">
                    <v-list-item-icon>
                      <v-icon>mdi-file-pdf-box</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>PDF</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    :href="'#/admin/print_rate_work_college_std/'"
                    target="_blank"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-printer</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>พิมพ์</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-subtitle>

        <!-- Data Table -->
        <v-data-table
          :loading="loading"
          :headers="enhancedHeaders"
          :items="filteredData"
          :search="search"
          :items-per-page="15"
          :footer-props="{
            'items-per-page-options': [10, 15, 25, 50, -1],
            'items-per-page-text': 'รายการต่อหน้า:'
          }"
          loading-text="กำลังโหลดข้อมูล..."
          no-data-text="ไม่มีข้อมูล"
          class="enhanced-table"
          fixed-header
          height="500"
        >
          <!-- College Name with Code -->
          <template v-slot:item.user_firstname="{ item }">
            <div>
              <div class="font-weight-bold">{{ item.user_firstname }}</div>
              <div class="text-caption text--secondary">
                รหัส: {{ item.user_code }}
              </div>
            </div>
          </template>

          <!-- Student Count Cells with Color Coding -->
          <template v-slot:item.ch165="{ item }">
            <v-chip small :color="getCountColor(item.ch165)" text-color="white">
              {{ item.ch165 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.ch265="{ item }">
            <v-chip small :color="getCountColor(item.ch265)" text-color="white">
              {{ item.ch265 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.ch365="{ item }">
            <v-chip small :color="getCountColor(item.ch365)" text-color="white">
              {{ item.ch365 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.sh165="{ item }">
            <v-chip small :color="getCountColor(item.sh165)" text-color="white">
              {{ item.sh165 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.sh265="{ item }">
            <v-chip small :color="getCountColor(item.sh265)" text-color="white">
              {{ item.sh265 || 0 }}
            </v-chip>
          </template>

          <!-- Year 66 -->
          <template v-slot:item.ch166="{ item }">
            <v-chip small :color="getCountColor(item.ch166)" text-color="white">
              {{ item.ch166 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.ch266="{ item }">
            <v-chip small :color="getCountColor(item.ch266)" text-color="white">
              {{ item.ch266 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.ch366="{ item }">
            <v-chip small :color="getCountColor(item.ch366)" text-color="white">
              {{ item.ch366 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.sh166="{ item }">
            <v-chip small :color="getCountColor(item.sh166)" text-color="white">
              {{ item.sh166 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.sh266="{ item }">
            <v-chip small :color="getCountColor(item.sh266)" text-color="white">
              {{ item.sh266 || 0 }}
            </v-chip>
          </template>

          <!-- Year 67 -->
          <template v-slot:item.ch167="{ item }">
            <v-chip small :color="getCountColor(item.ch167)" text-color="white">
              {{ item.ch167 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.ch267="{ item }">
            <v-chip small :color="getCountColor(item.ch267)" text-color="white">
              {{ item.ch267 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.ch367="{ item }">
            <v-chip small :color="getCountColor(item.ch367)" text-color="white">
              {{ item.ch367 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.sh167="{ item }">
            <v-chip small :color="getCountColor(item.sh167)" text-color="white">
              {{ item.sh167 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.sh267="{ item }">
            <v-chip small :color="getCountColor(item.sh267)" text-color="white">
              {{ item.sh267 || 0 }}
            </v-chip>
          </template>

          <!-- Short Course -->
          <template v-slot:item.sc66="{ item }">
            <v-chip small color="primary" text-color="white">
              {{ item.sc66 || 0 }}
            </v-chip>
          </template>

          <template v-slot:item.sc67="{ item }">
            <v-chip small color="primary" text-color="white">
              {{ item.sc67 || 0 }}
            </v-chip>
          </template>

          <!-- Total Row -->
          <template v-slot:item.total="{ item }">
            <v-chip color="primary" text-color="white" class="font-weight-bold">
              {{ calculateTotal(item) }}
            </v-chip>
          </template>

          <!-- No Results -->
          <template v-slot:no-results>
            <v-alert
              :value="true"
              color="info"
              icon="mdi-information"
              class="ma-4"
            >
              ไม่พบผลลัพธ์สำหรับ "{{ search }}"
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      <v-icon left>{{ snackbar.icon }}</v-icon>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          ปิด
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import VueMoment from "vue-moment";
import moment from "moment-timezone";

export default {
  name: "rate_work_std02",

  data() {
    return {
      VueMoment,
      moment,
      loading: false,
      ApiKey: "HRvec2021",
      viewMode: "table", // 'table' or 'chart'

      // Data
      rate_work_std02s: [],
      search: "",
      filterYear: null,

      // Chart Configuration
      selectedChartType: "bar",
      selectedYear: "67",
      selectedLevel: "all",

      chartTypes: [
        { text: "กราฟแท่ง", value: "bar" },
        { text: "กราฟเส้น", value: "line" },
        { text: "กราฟพื้นที่", value: "area" },
        { text: "กราฟวงกลม", value: "pie" }
      ],

      availableYears: [
        { text: "ปี 65", value: "65" },
        { text: "ปี 66", value: "66" },
        { text: "ปี 67", value: "67" },
        { text: "ทุกปี", value: "all" }
      ],

      levels: [
        { text: "ทุกระดับ", value: "all" },
        { text: "ปวช.", value: "ch" },
        { text: "ปวส.", value: "sh" },
        { text: "ระยะสั้น", value: "sc" }
      ],

      yearFilterOptions: [
        { text: "ปี 65", value: "65" },
        { text: "ปี 66", value: "66" },
        { text: "ปี 67", value: "67" }
      ],

      // Snackbar
      snackbar: {
        show: false,
        color: "success",
        timeout: 3000,
        icon: "mdi-check",
        text: ""
      },

      // Enhanced Headers
      enhancedHeaders: [
        {
          text: "วิทยาลัย",
          align: "left",
          value: "user_firstname",
          width: "200",
          sortable: true
        },
        {
          text: "ปวช.1/65",
          align: "center",
          value: "ch165",
          width: "100",
          sortable: true
        },
        {
          text: "ปวช.2/65",
          align: "center",
          value: "ch265",
          width: "100",
          sortable: true
        },
        {
          text: "ปวช.3/65",
          align: "center",
          value: "ch365",
          width: "100",
          sortable: true
        },
        {
          text: "ปวส.1/65",
          align: "center",
          value: "sh165",
          width: "100",
          sortable: true
        },
        {
          text: "ปวส.2/65",
          align: "center",
          value: "sh265",
          width: "100",
          sortable: true
        },
        {
          text: "ปวช.1/66",
          align: "center",
          value: "ch166",
          width: "100",
          sortable: true
        },
        {
          text: "ปวช.2/66",
          align: "center",
          value: "ch266",
          width: "100",
          sortable: true
        },
        {
          text: "ปวช.3/66",
          align: "center",
          value: "ch366",
          width: "100",
          sortable: true
        },
        {
          text: "ปวส.1/66",
          align: "center",
          value: "sh166",
          width: "100",
          sortable: true
        },
        {
          text: "ปวส.2/66",
          align: "center",
          value: "sh266",
          width: "100",
          sortable: true
        },
        {
          text: "ปวช.1/67",
          align: "center",
          value: "ch167",
          width: "100",
          sortable: true
        },
        {
          text: "ปวช.2/67",
          align: "center",
          value: "ch267",
          width: "100",
          sortable: true
        },
        {
          text: "ปวช.3/67",
          align: "center",
          value: "ch367",
          width: "100",
          sortable: true
        },
        {
          text: "ปวส.1/67",
          align: "center",
          value: "sh167",
          width: "100",
          sortable: true
        },
        {
          text: "ปวส.2/67",
          align: "center",
          value: "sh267",
          width: "100",
          sortable: true
        },
        {
          text: "ระยะสั้น 66",
          align: "center",
          value: "sc66",
          width: "100",
          sortable: true
        },
        {
          text: "ระยะสั้น 67",
          align: "center",
          value: "sc67",
          width: "100",
          sortable: true
        },
        {
          text: "รวม",
          align: "center",
          value: "total",
          width: "100",
          sortable: true
        }
      ]
    };
  },

  async mounted() {
    await this.rate_work_std02QueryAll();
  },

  methods: {
    async rate_work_std02QueryAll() {
      this.loading = true;
      try {
        const result = await this.$http.post("rate_work_std02.php", {
          ApiKey: this.ApiKey
        });
        this.rate_work_std02s = result.data || [];       
        this.showSnackbar("โหลดข้อมูลเรียบร้อยแล้ว", "success");
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async refreshData() {
      await this.rate_work_std02QueryAll();
    },

    calculateTotal(item) {
      const fields = [
        "ch165",
        "ch265",
        "ch365",
        "sh165",
        "sh265",
        "ch166",
        "ch266",
        "ch366",
        "sh166",
        "sh266",
        "ch167",
        "ch267",
        "ch367",
        "sh167",
        "sh267",
        "sc66",
        "sc67"
      ];

      return fields.reduce((sum, field) => {
        return sum + (parseInt(item[field]) || 0);
      }, 0);
    },

    getCountColor(count) {
      const num = parseInt(count) || 0;
      if (num === 0) return "grey";
      if (num <= 50) return "green";
      if (num <= 100) return "blue";
      if (num <= 200) return "orange";
      return "red";
    },

    exportToExcel() {
      // Export functionality
      this.showSnackbar("กำลังส่งออกไฟล์ Excel...", "info");
      // Implementation for Excel export
    },

    exportToPDF() {
      // Export functionality
      this.showSnackbar("กำลังส่งออกไฟล์ PDF...", "info");
      // Implementation for PDF export
    },

    showSnackbar(text, color = "success") {
      const iconMap = {
        success: "mdi-check-circle",
        error: "mdi-alert-circle",
        warning: "mdi-alert",
        info: "mdi-information"
      };

      this.snackbar = {
        show: true,
        text,
        color,
        icon: iconMap[color] || "mdi-information",
        timeout: 3000
      };
    }
  },

  computed: {
    filteredData() {
      let data = [...this.rate_work_std02s];

      if (this.filterYear) {
        // Filter by year logic can be implemented here
      }

      return data;
    },

    summaryStats() {
      const data = this.rate_work_std02s;

      const totalStudents65 = data.reduce((sum, item) => {
        return (
          sum +
          (parseInt(item.ch165) || 0) +
          (parseInt(item.ch265) || 0) +
          (parseInt(item.ch365) || 0) +
          (parseInt(item.sh165) || 0) +
          (parseInt(item.sh265) || 0)
        );
      }, 0);

      const totalStudents66 = data.reduce((sum, item) => {
        return (
          sum +
          (parseInt(item.ch166) || 0) +
          (parseInt(item.ch266) || 0) +
          (parseInt(item.ch366) || 0) +
          (parseInt(item.sh166) || 0) +
          (parseInt(item.sh266) || 0)
        );
      }, 0);

      const totalStudents67 = data.reduce((sum, item) => {
        return (
          sum +
          (parseInt(item.ch167) || 0) +
          (parseInt(item.ch267) || 0) +
          (parseInt(item.ch367) || 0) +
          (parseInt(item.sh167) || 0) +
          (parseInt(item.sh267) || 0)
        );
      }, 0);

      const totalShortCourse = data.reduce((sum, item) => {
        return sum + (parseInt(item.sc66) || 0) + (parseInt(item.sc67) || 0);
      }, 0);

      return [
        {
          title: "นักเรียน ปี 65",
          value: totalStudents65.toLocaleString(),
          icon: "mdi-account-group",
          color: "blue"
        },
        {
          title: "นักเรียน ปี 66",
          value: totalStudents66.toLocaleString(),
          icon: "mdi-account-group",
          color: "green"
        },
        {
          title: "นักเรียน ปี 67",
          value: totalStudents67.toLocaleString(),
          icon: "mdi-account-group",
          color: "orange"
        },
        {
          title: "หลักสูตรระยะสั้น",
          value: totalShortCourse.toLocaleString(),
          icon: "mdi-school",
          color: "primary"
        }
      ];
    },

    chartTitle() {
      const yearText =
        this.selectedYear === "all" ? "ทุกปี" : `ปี ${this.selectedYear}`;
      const levelText =
        this.levels.find(l => l.value === this.selectedLevel).text ||
        "ทุกระดับ";
      return `สถิตินักเรียนนักศึกษา ${levelText} ${yearText}`;
    },

    chartSeries() {
      if (!this.rate_work_std02s.length) return [];

      // Generate chart data based on selected filters
      const data = this.rate_work_std02s.slice(0, 10); // Show top 10 colleges

      if (this.selectedChartType === "pie") {
        return [
          {
            name: "จำนวนนักเรียน",
            data: data.map(item => this.calculateTotal(item))
          }
        ];
      }

      return [
        {
          name: "จำนวนนักเรียน",
          data: data.map(item => this.calculateTotal(item))
        }
      ];
    },

    chartOptions() {
      const categories = this.rate_work_std02s
        .slice(0, 10)
        .map(item => item.user_firstname);

      const baseOptions = {
        chart: {
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          }
        },
        colors: ["#B3151B", "#424242", "#E57373", "#9E9E9E"],
        dataLabels: {
          enabled: true,
          style: {
            fontSize: "12px",
            fontWeight: "bold"
          }
        },
        title: {
          text: this.chartTitle,
          align: "center",
          style: {
            fontSize: "16px",
            fontWeight: "bold"
          }
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                height: 300
              },
              legend: {
                position: "bottom"
              }
            }
          }
        ]
      };

      if (this.selectedChartType === "pie") {
        return {
          ...baseOptions,
          labels: categories,
          legend: {
            position: "bottom"
          }
        };
      }

      return {
        ...baseOptions,
        xaxis: {
          categories,
          labels: {
            rotate: -45,
            style: {
              fontSize: "10px"
            }
          }
        },
        yaxis: {
          title: {
            text: "จำนวนนักเรียน (คน)"
          }
        },
        grid: {
          borderColor: "#e0e0e0",
          strokeDashArray: 5
        }
      };
    }
  }
};
</script>

<style scoped>
.student-statistics-container {
  padding: 16px;
  background: linear-gradient(135deg, #f5f5f5 0%, #90a4ae 100%);
  min-height: 100vh;
}

.stat-card {
  border-radius: 15px !important;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
}

.chart-container {
  border-radius: 15px !important;
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
}

.search-field {
  border-radius: 25px;
}

.enhanced-table {
  border-radius: 0 0 15px 15px;
  background: white;
}

.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 0 0 15px 15px;
}

.enhanced-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.enhanced-table >>> .v-data-table-header th {
  background: linear-gradient(145deg, #455a64 0%, #455a64 100%) !important;
  color: white !important;
  font-weight: 600;
  border: none !important;
}

.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.v-btn-toggle {
  border-radius: 25px !important;
  overflow: hidden;
}

.v-btn-toggle >>> .v-btn {
  border-radius: 0 !important;
}

/* Export button styling */
.v-menu >>> .v-list {
  border-radius: 10px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.v-menu >>> .v-list-item:hover {
  background: linear-gradient(145deg, #f5f5f5 0%, #e8e8e8 100%);
}

/* Custom scrollbar for table */
.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Chart container animations */
.chart-container {
  transition: all 0.3s ease;
}

.chart-container:hover {
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.1);
}

/* Loading states */
.v-data-table >>> .v-data-table__progress {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Snackbar styling */
.v-snackbar >>> .v-snack__wrapper {
  border-radius: 10px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Button hover effects */
.v-btn {
  transition: all 0.3s ease;
}

.v-btn:hover:not(.v-btn--disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

/* Card title styling */
.v-card-title {
  background: linear-gradient(145deg, #455a64 0%, #455a64 100%);
  border-radius: 15px 15px 0 0;
}

/* Form field styling */
.v-text-field--outlined >>> fieldset,
.v-select--outlined >>> fieldset {
  border-color: #e0e0e0;
  border-radius: 15px;
}

.v-text-field--outlined:hover >>> fieldset,
.v-select--outlined:hover >>> fieldset {
  border-color: #455a64;
}

.v-text-field--outlined.v-input--is-focused >>> fieldset,
.v-select--outlined.v-input--is-focused >>> fieldset {
  border-color: #455a64;
  border-width: 2px;
}

/* Responsive design */
@media (max-width: 1024px) {
  .enhanced-table >>> .v-data-table__wrapper {
    max-height: 500px;
  }

  .chart-container {
    margin-top: 16px;
  }
}

@media (max-width: 768px) {
  .student-statistics-container {
    padding: 8px;
  }

  .stat-card {
    margin-bottom: 8px;
  }

  .enhanced-table >>> .v-data-table__wrapper {
    max-height: 400px;
  }

  .v-card-title {
    font-size: 1.1rem !important;
  }

  .v-card-title .text-h5 {
    font-size: 1.1rem !important;
  }

  /* Hide some columns on mobile */
  .enhanced-table >>> th:nth-child(n + 6),
  .enhanced-table >>> td:nth-child(n + 6) {
    display: none;
  }

  /* Show only essential columns on mobile */
  .enhanced-table >>> th:nth-child(1),
  .enhanced-table >>> th:nth-child(2),
  .enhanced-table >>> th:nth-child(3),
  .enhanced-table >>> th:nth-child(-1),
  .enhanced-table >>> td:nth-child(1),
  .enhanced-table >>> td:nth-child(2),
  .enhanced-table >>> td:nth-child(3),
  .enhanced-table >>> td:nth-child(-1) {
    display: table-cell;
  }
}

@media (max-width: 480px) {
  .v-btn-toggle {
    flex-direction: column;
    width: 100%;
  }

  .v-btn-toggle >>> .v-btn {
    width: 100%;
    margin-bottom: 4px;
  }

  .stat-card >>> .v-card-text {
    padding: 12px;
  }

  .stat-card >>> .text-h4 {
    font-size: 1.5rem !important;
  }
}

/* Print styles */
@media print {
  .student-statistics-container {
    background: white !important;
    padding: 0;
  }

  .v-btn,
  .search-field,
  .v-btn-toggle {
    display: none !important;
  }

  .v-card {
    box-shadow: none !important;
    border: 1px solid #ddd;
  }

  .enhanced-table >>> .v-data-table__wrapper {
    max-height: none !important;
  }
}

/* Dark theme support */
.theme--dark .chart-container {
  background: linear-gradient(145deg, #2d2d2d 0%, #1e1e1e 100%);
}

.theme--dark .enhanced-table {
  background: #2d2d2d;
}

.theme--dark .stat-card {
  background: linear-gradient(145deg, #2d2d2d 0%, #1e1e1e 100%);
}

/* Accessibility improvements */
.v-btn:focus,
.v-text-field:focus,
.v-select:focus {
  outline: 2px solid #455a64;
  outline-offset: 2px;
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .v-chip {
    border: 2px solid currentColor;
  }

  .enhanced-table >>> tbody tr:hover {
    background-color: #000 !important;
    color: #fff !important;
  }
}

/* Reduced motion for accessibility */
@media (prefers-reduced-motion: reduce) {
  .stat-card:hover,
  .v-btn:hover,
  .v-chip:hover {
    transform: none;
  }

  .v-data-table >>> .v-data-table__progress {
    animation: none;
  }
}
</style>
